import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import type { UseMutationResult } from "@tanstack/react-query";
import type {
  ClientJobRole,
  ClientUser,
  EnabledServices,
  ServiceOverrides,
} from "../api/clientManagement";

type ServiceKey = keyof EnabledServices;

interface UserEditDialogProps {
  open: boolean;
  user: ClientUser | null;
  isCentralAdmin: boolean;
  departments: { id: string; name: string }[];
  jobRoles: ClientJobRole[];
  updateUserServicesMutation: UseMutationResult<
    unknown,
    unknown,
    { userId: string; payload: Partial<ServiceOverrides> }
  >;
  updatingUserServicesId: string | null;
  assignUserToDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; userId: string }
  >;
  removeUserFromDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; userId: string }
  >;
  assignUserToJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; userId: string }
  >;
  removeUserFromJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; userId: string }
  >;
  removeUserFromClientMutation: UseMutationResult<unknown, unknown, string>;
  onClose: () => void;
}

export const UserEditDialog: React.FC<UserEditDialogProps> = ({
  open,
  user,
  isCentralAdmin,
  departments,
  jobRoles,
  updateUserServicesMutation,
  updatingUserServicesId,
  assignUserToDepartmentMutation,
  removeUserFromDepartmentMutation,
  assignUserToJobRoleMutation,
  removeUserFromJobRoleMutation,
  removeUserFromClientMutation,
  onClose,
}) => {
  const [departmentSelection, setDepartmentSelection] = useState("");
  const [jobRoleSelection, setJobRoleSelection] = useState("");
  const [serviceOverrides, setServiceOverrides] = useState<ServiceOverrides>({
    learning_locker: null,
    occupational_health: null,
    wearables: null,
    akea_life: null,
  });

  useEffect(() => {
    if (!user || !open) return;
    setDepartmentSelection("");
    setJobRoleSelection("");
    setServiceOverrides({
      learning_locker: user.service_overrides?.learning_locker ?? null,
      occupational_health: user.service_overrides?.occupational_health ?? null,
      wearables: user.service_overrides?.wearables ?? null,
      akea_life: user.service_overrides?.akea_life ?? null,
    });
  }, [open, user]);

  if (!user) return null;

  const isSavingServices = updatingUserServicesId === user.id;
  const enabledServices = user.enabled_services ?? {
    learning_locker: false,
    occupational_health: false,
    wearables: false,
    akea_life: false,
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6">
            Edit {user.first_name} {user.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Alert severity="info">
            Update department, job role and services for this user.
          </Alert>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Departments</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <FormControl size="small" fullWidth>
                <Select
                  value={departmentSelection}
                  displayEmpty
                  onChange={(event) => setDepartmentSelection(event.target.value)}
                  renderValue={(value) => {
                    if (!value) return "Select department";
                    return departments.find((dept) => dept.id === value)?.name ?? value;
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                disabled={!departmentSelection}
                onClick={() =>
                  assignUserToDepartmentMutation.mutate({
                    departmentId: departmentSelection,
                    userId: user.id,
                  })
                }
              >
                Assign
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {user.departments.length ? (
                user.departments.map((dept) => (
                  <Button
                    key={dept.id}
                    variant="text"
                    size="small"
                    onClick={() =>
                      removeUserFromDepartmentMutation.mutate({
                        departmentId: dept.id,
                        userId: user.id,
                      })
                    }
                  >
                    Remove {dept.name}
                  </Button>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No departments assigned.
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Job Roles</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <FormControl size="small" fullWidth>
                <Select
                  value={jobRoleSelection}
                  displayEmpty
                  onChange={(event) => setJobRoleSelection(event.target.value)}
                  renderValue={(value) => {
                    if (!value) return "Select job role";
                    return jobRoles.find((role) => role.id === value)?.name ?? value;
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {jobRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                disabled={!jobRoleSelection}
                onClick={() =>
                  assignUserToJobRoleMutation.mutate({
                    jobRoleId: jobRoleSelection,
                    userId: user.id,
                  })
                }
              >
                Assign
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {user.job_roles.length ? (
                user.job_roles.map((role) => (
                  <Button
                    key={role.id}
                    variant="text"
                    size="small"
                    onClick={() =>
                      removeUserFromJobRoleMutation.mutate({
                        jobRoleId: role.id,
                        userId: user.id,
                      })
                    }
                  >
                    Remove {role.name}
                  </Button>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No job roles assigned.
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Services</Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              {SERVICE_FIELDS.map((service) => {
                const value = toSelectValue(serviceOverrides[service.key]);
                const effectiveLabel = enabledServices[service.key]
                  ? "Enabled"
                  : "Disabled";
                return (
                  <FormControl key={service.key} size="small" fullWidth>
                    <Select
                      value={value}
                      onChange={(event) =>
                        setServiceOverrides((prev) => ({
                          ...prev,
                          [service.key]: fromSelectValue(event.target.value),
                        }))
                      }
                      disabled={!isCentralAdmin || isSavingServices}
                    >
                      <MenuItem value="default">Use client default</MenuItem>
                      <MenuItem value="enabled">Enabled</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                    <FormHelperText>
                      {service.label}: {effectiveLabel}
                    </FormHelperText>
                  </FormControl>
                );
              })}
            </Box>
            {isCentralAdmin ? (
              <Button
                variant="outlined"
                onClick={() =>
                  updateUserServicesMutation.mutate({
                    userId: user.id,
                    payload: serviceOverrides,
                  })
                }
                disabled={isSavingServices}
              >
                Save Services
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button
          color="error"
          onClick={() =>
            removeUserFromClientMutation.mutate(user.id, { onSuccess: onClose })
          }
        >
          Remove From Client
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const SERVICE_FIELDS: { key: ServiceKey; label: string }[] = [
  { key: "learning_locker", label: "Learning Locker" },
  { key: "occupational_health", label: "Occupational Health" },
  { key: "wearables", label: "Wearables" },
  { key: "akea_life", label: "Akea Life" },
];

const toSelectValue = (
  value: boolean | null,
): "default" | "enabled" | "disabled" => {
  if (value === null) return "default";
  return value ? "enabled" : "disabled";
};

const fromSelectValue = (value: unknown): boolean | null => {
  if (value === "enabled") return true;
  if (value === "disabled") return false;
  return null;
};
